import api from '../../lib/api';

export const FETCH_TEMPLATES = 'FETCH_TEMPLATES';
export const FETCH_TEMPLATE = 'FETCH_TEMPLATE';
export const CREATE_TEMPLATE = 'CREATE_TEMPLATE';
export const UPDATE_TEMPLATE = 'UPDATE_TEMPLATE';
export const DELETE_TEMPLATE = 'DELETE_TEMPLATE';
export const PREVIEW_TEMPLATE = 'PREVIEW_TEMPLATE';

// Константы движков рендеринга
export const RENDER_ENGINES = {
  LEGACY: 'legacy',
  JINJA2: 'jinja2',
};

// Константы типов расположения
export const PLACEMENT_TYPES = {
  MODAL_CENTER: 'MODAL_CENTER',
  SLIDER_RIGHT: 'SLIDER_RIGHT',
  SLIDER_LEFT: 'SLIDER_LEFT',
  BANNER_TOP: 'BANNER_TOP',
  BANNER_BOTTOM: 'BANNER_BOTTOM',
};

// Константы типов шаблонов
export const TEMPLATE_TYPES = {
  ON_SITE: 'ON_SITE',
  INBOX: 'INBOX',
};

const TEMPLATES_LIST_LOADING_CHANGE = 'TEMPLATES_LIST_LOADING_CHANGE';
const TEMPLATES_LIST_CHANGE = 'TEMPLATES_LIST_CHANGE';
const TEMPLATE_CHANGE = 'TEMPLATE_CHANGE';
const TEMPLATE_LOADING_CHANGE = 'TEMPLATE_LOADING_CHANGE';
const TEMPLATE_ERRORS_CHANGE = 'TEMPLATE_ERRORS_CHANGE';
const TEMPLATE_SAVING_CHANGE = 'TEMPLATE_SAVING_CHANGE';
const TEMPLATE_DELETING_CHANGE = 'TEMPLATE_DELETING_CHANGE';
const TEMPLATE_PREVIEW_LOADING_CHANGE = 'TEMPLATE_PREVIEW_LOADING_CHANGE';
const TEMPLATE_PREVIEW_CHANGE = 'TEMPLATE_PREVIEW_CHANGE';
const TEMPLATE_PREVIEW_ERRORS_CHANGE = 'TEMPLATE_PREVIEW_ERRORS_CHANGE';

// Шаблон пустого уведомления
const template_empty = (type = TEMPLATE_TYPES.ON_SITE) => {
  if (type === TEMPLATE_TYPES.INBOX) {
    return {
      name: '',
      type: TEMPLATE_TYPES.INBOX,
      is_active: true,
      render_engine: RENDER_ENGINES.JINJA2,
      content: {
        title: '',
        content: '',
        html: '',
      },
      config: {
        ttl_hours: 168,
      },
    };
  }

  return {
    name: '',
    type: TEMPLATE_TYPES.ON_SITE,
    is_active: true,
    render_engine: RENDER_ENGINES.JINJA2,
    content: { html: '' },
    config: { display_variant: PLACEMENT_TYPES.MODAL_CENTER },
  };
};

const state = {
  templates_list: [],
  templates_list_loading: false,
  templates_list_total: 0,
  templates_list_per_page: 20,
  templates_list_page: 1,

  template: template_empty(),
  template_loading: false,
  template_saving: false,
  template_errors: {},
  template_deleting: false,

  // Preview состояние
  template_preview: '',
  template_preview_loading: false,
  template_preview_errors: {},

  // Енамы типов расположения
  placement_types: PLACEMENT_TYPES,

  // Енамы движков рендеринга
  render_engines: RENDER_ENGINES,
};

const actions = {
  /**
   * Загружает список шаблонов уведомлений
   * @param {Object} options - Дополнительные опции (например, заголовки)
   */
  [FETCH_TEMPLATES]({ commit, state }, { data = {}, options = {} }) {
    return api.list(
      'notification_templates.list',
      '',
      data,
      state,
      commit,
      TEMPLATES_LIST_LOADING_CHANGE,
      TEMPLATES_LIST_CHANGE,
      null,
      options,
    );
  },

  /**
   * Загружает данные шаблона по ID
   * @param {Number|String|Object} param - ID шаблона или объект с параметрами
   * @param {Number|String} param.id - ID шаблона
   * @param {Object} param.options - Дополнительные опции (например, заголовки)
   */
  [FETCH_TEMPLATE]({ commit, state }, param) {
    const id = param.id;
    const options = param.options || {};

    return api.load(
      'notification_template',
      id,
      state,
      commit,
      TEMPLATE_LOADING_CHANGE,
      TEMPLATE_CHANGE,
      TEMPLATE_ERRORS_CHANGE,
      options,
    );
  },

  /**
   * Создает новый шаблон
   * @param {Object|String} param - Данные шаблона или объект с параметрами
   * @param {Object} param.data - Данные шаблона
   * @param {Object} param.options - Дополнительные опции (например, заголовки)
   */
  [CREATE_TEMPLATE]({ commit, state }, param) {
    const data = param.data;
    const options = param.options || {};

    return api.create(
      'notification_template.create',
      '',
      data,
      state,
      commit,
      TEMPLATE_SAVING_CHANGE,
      TEMPLATE_CHANGE,
      TEMPLATE_ERRORS_CHANGE,
      options,
    );
  },

  /**
   * Обновляет шаблон
   * @param {Object} param - Параметры запроса
   * @param {Number|String} param.id - ID шаблона
   * @param {Object} param.data - Данные шаблона
   * @param {Object} param.options - Дополнительные опции (например, заголовки)
   */
  [UPDATE_TEMPLATE]({ commit, state }, param) {
    const id = param.id;
    const data = param.data;
    const options = param.options || {};

    return api.update(
      'notification_template.update',
      id,
      data,
      state,
      commit,
      TEMPLATE_SAVING_CHANGE,
      TEMPLATE_CHANGE,
      TEMPLATE_ERRORS_CHANGE,
      options,
    );
  },

  /**
   * Удаляет шаблон
   * @param {Number|String|Object} param - ID шаблона или объект с параметрами
   * @param {Number|String} param.id - ID шаблона
   * @param {Object} param.options - Дополнительные опции (например, заголовки)
   */
  [DELETE_TEMPLATE]({ commit, state }, param) {
    const id = param.id;
    const options = param.options || {};

    commit(TEMPLATE_DELETING_CHANGE, true);
    return api.delete(
      'notification_template.delete',
      id,
      state,
      commit,
      TEMPLATE_CHANGE,
      options,
    ).finally(() => {
      commit(TEMPLATE_DELETING_CHANGE, false);
    });
  },

  /**
   * Выполняет предпросмотр шаблона с реальными данными
   * @param {Object} param - Параметры запроса
   * @param {String} param.order_key - Ключ заказа для получения данных
   * @param {String} param.template_content - HTML-содержимое шаблона
   * @param {String} param.render_engine - Движок рендеринга (legacy/jinja2)
   * @param {Object} param.options - Дополнительные опции
   * @returns {Promise} - Promise результата запроса
   */
  [PREVIEW_TEMPLATE]({ commit, state }, param) {
    const data = {
      order_key: param.order_key,
      template_content: param.content,
      render_engine: param.render_engine || RENDER_ENGINES.JINJA2,
    };
    const options = param.options || {};

    commit(TEMPLATE_PREVIEW_LOADING_CHANGE, true);
    commit(TEMPLATE_PREVIEW_ERRORS_CHANGE, {});

    return api.create(
      'notification_template.preview',
      '',
      data,
      state,
      commit,
      TEMPLATE_PREVIEW_LOADING_CHANGE,
      TEMPLATE_PREVIEW_CHANGE,
      TEMPLATE_PREVIEW_ERRORS_CHANGE,
      options,
    );
  },
};

const mutations = {
  [TEMPLATES_LIST_LOADING_CHANGE](state, data) {
    state.templates_list_loading = data;
  },

  [TEMPLATES_LIST_CHANGE](state, data) {
    if (data?.data) {
      state.templates_list = data.data;
      state.templates_list_total = data.total;
      state.templates_list_per_page = data.limit;
      state.templates_list_page = Math.ceil(data.offset / data.limit) + 1;
    } else {
      state.templates_list = [];
      state.templates_list_total = 0;
      state.templates_list_per_page = 20;
      state.templates_list_page = 1;
    }
  },

  [TEMPLATE_CHANGE](state, data) {
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      state.template = data;
    } else {
      state.template = template_empty();
    }
  },

  [TEMPLATE_LOADING_CHANGE](state, data) {
    state.template_loading = data;
  },

  [TEMPLATE_SAVING_CHANGE](state, data) {
    state.template_saving = data;
  },

  [TEMPLATE_ERRORS_CHANGE](state, data) {
    state.template_errors = data || {};
  },

  [TEMPLATE_DELETING_CHANGE](state, data) {
    state.template_deleting = data;
  },

  [TEMPLATE_PREVIEW_LOADING_CHANGE](state, data) {
    state.template_preview_loading = data;
  },

  [TEMPLATE_PREVIEW_CHANGE](state, data) {
    state.template_preview = data?.rendered_content || data || '';
  },

  [TEMPLATE_PREVIEW_ERRORS_CHANGE](state, data) {
    state.template_preview_errors = data || {};
  },
};

const getters = {
  hasErrors: (state) => Object.keys(state.template_errors).length > 0,
  hasPreviewErrors: (state) => Object.keys(state.template_preview_errors).length > 0,
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
